import '../hint/ui-hint';
import { UIElement } from '../ui-element';
import { Component } from '../../decorators/component.decorator';
import { Attribute } from '../../decorators/attribute.decorator';
import { Child } from '../../decorators/child.decorator';
import { setUnsafeInnerHTML } from '../../global/render-api';
import type {
    AttributeValue,
    ElementChild,
    ElementConfig,
    ObservedAttributes,
} from '../ui-element.type';
import styles from './ui-progress.css';

/**
 * @class
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIProgress
 * @element ui-progress
 * @classdesc Represents a class for <code>ui-progress</code> element.
 * Similar to ui-slider, but it it only read-only. This is a kind of
 * 'read-only' slider.
 * @property {number} [value=0] {@attr value} Value of progress.
 * @property {number} [min=0]  {@attr min} Minumum of progress.
 * @property {number} [max=100] {@attr max} Maximum of progress.
 * @property {number} [accuracy=2] {@attr accuracy} Formats a number using fixed-point notation
 * (-1 to disable accuracy).
 * @property {string} [labelLeft] {@attr label-left} Label of the text left value.
 * @property {string} [labelMiddle] {@attr label-middle} Label of the text middle value.
 * @property {string} [labelRight] {@attr label-right} Label of the text right value.
 * @property {string} [units] {@attr units} Label units like 'EUR', 'USD', '%', etc.
 * @property {HTMLDivElement} progressBar
 * @property {HTMLDivElement} currentValue
 * @property {HTMLDivElement} leftValue
 * @property {HTMLDivElement} limit
 * @property {HTMLDivElement} leftHint
 * @property {HTMLDivElement} middleHint
 * @property {HTMLDivElement} rightHint
 * @example
 * <ui-progress value="3" min="0" max="100" units="EUR" size="medium"></ui-progress>
 */
@Component({ tag: 'ui-progress', styles })
export class UIProgress extends UIElement {
    /**
     * @type {Array<string>}
     */
    static get observedAttributes(): ObservedAttributes {
        return [
            'min',
            'max',
            'value',
            'units',
            'label-left',
            'label-middle',
            'label-right',
        ];
    }

    @Attribute(Number) value: number = 0;
    @Attribute(Number) min: number = 0;
    @Attribute(Number) max: number = 100;
    @Attribute(Number) accuracy: number = 2;
    @Attribute(String) labelLeft?: string;
    @Attribute(String) labelMiddle?: string;
    @Attribute(String) labelRight?: string;
    @Attribute(String) units: string = '%';

    @Child('.ui-progress__progress') progressBar!: HTMLDivElement;
    @Child('.ui-progress__value.-current') currentValue!: HTMLDivElement;
    @Child('.ui-progress__value.-left') leftValue!: HTMLDivElement;
    @Child('.ui-progress__hint.-start') leftHint!: HTMLDivElement;
    @Child('.ui-progress__hint.-middle') middleHint!: HTMLDivElement;
    @Child('.ui-progress__hint.-end') rightHint!: HTMLDivElement;
    @Child('.ui-progress__limit') limit!: HTMLDivElement;

    /**
     * Format value base on given currency
     * @param {number|string} value
     * @returns {string}
     */
    formatValue(value: number | string): string {
        let numberValue = Number(value);
        if (this.accuracy > -1) {
            numberValue = Number(numberValue.toFixed(this.accuracy));
        }

        switch (this.units) {
            case '€':
            case '$':
                return [
                    String(numberValue || 0).replace(
                        /(\d)(?=(\d{3})+(?!\d))/g,
                        '$1 '
                    ),
                    this.units,
                ].join(' ');
            default:
                return [String(numberValue), this.units].join(' ');
        }
    }

    /**
     * Sets an absolute value to the controls.
     * @param {number} value
     * @returns {UIElement | UISlider}
     */
    setValue(value: number): UIProgress {
        this.value = value;
        if (this.value > this.max) {
            this.value = this.max;
        }
        if (this.value < this.min) {
            this.value = this.min;
        }

        this.currentValue.textContent = this.formatValue(this.value);
        this.leftValue.textContent = this.formatValue(this.max - this.value);
        this.setAttribute('aria-valuenow', String(this.value));
        this.setAttribute('aria-valuetext', this.formatValue(this.value));
        this.setAttribute('aria-valuemin', String(this.min));
        this.setAttribute('aria-valuemax', String(this.max));
        return this;
    }

    /**
     * Sets offset for progress bar.
     * @param {number} progress
     * @returns {UIProgress}
     */
    setProgressPosition(progress: number): UIProgress {
        this.progressBar.style.transform =
            'translateX(-' + (100 - progress) + '%)';
        return this;
    }

    /**
     * Applies the current input value to user controls.
     * @returns {UIProgress}
     */
    updateValue(): UIProgress {
        this.setValue(this.value);
        this.setProgressPosition(this.calcProgress(this.value));
        return this;
    }

    /**
     * Update the limit label.
     * @returns {UIProgress}
     */
    updateLimit(): UIProgress {
        setUnsafeInnerHTML(this.limit, this.formatValue(this.max));
        return this;
    }

    /**
     * Calculates progress value based on the given absolute value
     * @param {number} value
     * @returns {number}
     */
    calcProgress(value: number): number {
        const min = Number(this.min);
        const max = Number(this.max);
        return ((value - min) / (max - min)) * 100;
    }

    /**
     * @inheritDoc
     */
    override observeAttributes(
        name: AttributeValue,
        oldValue: AttributeValue,
        newValue: string | null
    ): void {
        /* istanbul ignore if */
        if (!this.hydrated) {
            return;
        }
        switch (name) {
            case 'min':
            case 'value':
                this.updateValue();
                break;
            case 'units':
            case 'max':
                this.updateValue().updateLimit();
                break;
            case 'label-left':
                this.rebuild.call(this.leftHint, this.buildLeftHint());
                break;
            case 'label-middle':
                this.rebuild.call(this.middleHint, this.buildMiddleHint());
                break;
            case 'label-right':
                this.rebuild.call(this.rightHint, this.buildRightHint());
                break;
        }
    }

    /**
     * @inheritDoc
     */
    buildLeftHint(): ElementConfig {
        return {
            tagName: 'ui-hint',
            classList: {
                'ui-progress__hint': true,
                '-start': true,
            },
            children: [this.labelLeft as ElementChild],
        };
    }

    /**
     * @inheritDoc
     */
    buildRightHint(): ElementConfig {
        return {
            tagName: 'ui-hint',
            classList: {
                'ui-progress__hint': true,
                '-end': true,
            },
            children: [this.labelRight as ElementChild],
        };
    }

    /**
     * @inheritDoc
     */
    buildMiddleHint(): ElementConfig {
        return {
            tagName: 'div',
            classList: {
                'ui-progress__hint': true,
                '-middle': true,
            },
            children:
                this.labelMiddle !== 'off'
                    ? [
                          this.labelMiddle || '',
                          ' ',
                          {
                              tagName: 'strong',
                              classList: {
                                  'ui-progress__limit': true,
                              },
                              children: [this.formatValue(this.max)],
                          },
                      ]
                    : [],
        };
    }

    /**
     * @inheritDoc
     */
    override render(): void {
        this.setAttribute('role', 'progressbar');
        this.innerHTML = '';
        this.insertElements([
            {
                tagName: 'div',
                classList: {
                    'ui-progress__value': true,
                    '-current': true,
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-progress__value': true,
                    '-left': true,
                },
            },
            {
                tagName: 'div',
                classList: {
                    'ui-progress__track': true,
                },
                children: [
                    {
                        tagName: 'div',
                        classList: {
                            'ui-progress__progress': true,
                        },
                    },
                ],
            },
            {
                tagName: 'div',
                classList: {
                    'ui-progress__hints': true,
                },
                children: [
                    this.buildLeftHint(),
                    this.buildMiddleHint(),
                    this.buildRightHint(),
                ],
            },
        ]);
        this.updateValue();
    }
}
